import { useState } from 'react'

import { useToast } from '@/components/Toast'
import { CTAButton } from '@/components/ui/CTAButton'
import { DialogModal } from '@/components/ui/DialogModal'
import { getUserFacingMessage } from '@/lib/errorMessage'
import { BlockStockAlertModal, ProductCard } from '@/pages/SealedVinylPage/components'
import { useEditWindowExpiry } from '@/pages/SealedVinylPage/hooks/useEditWindowExpiry'
import type { BlockDialogKind } from '@/pages/SealedVinylPage/hooks/useProceedToPayment'
import { CHANGE_NOT_ALLOWED_DIALOG } from '@/pages/SealedVinylPage/lib/constants'
import { apiGetSealedItemStock } from '@/payment/api'
import { usePatchOrder } from '@/payment/query'
import type { GetSealedOrderDetailResponse } from '@/payment/types'

import { EditBottomBar, EditContainer, EditContents, EditSection, EditSectionHeading } from './styles'

interface EditFormProps {
  detail: GetSealedOrderDetailResponse
  onCompleted: () => void
}

export function EditForm({ detail, onCompleted }: EditFormProps) {
  const { orderInfo } = detail
  const isEditExpired = useEditWindowExpiry(orderInfo.registeredAt)

  const initialQuantity = orderInfo.quantity
  const [quantity, setQuantity] = useState(initialQuantity)
  const [isChecking, setIsChecking] = useState(false)
  const [blockDialog, setBlockDialog] = useState<BlockDialogKind | null>(null)

  const { mutate: patchOrder, isPending } = usePatchOrder()
  const { show: showToast } = useToast()

  const isChanged = quantity !== initialQuantity

  const handleSubmit = async () => {
    if (!isChanged || isEditExpired || isChecking || isPending) return

    // 변경 수량이 남은 재고를 초과하면 차단 — 신청/결제 단계와 동일 정책. PATCH 전에 선제 검증.
    // 판매중지·재고0 → soldOut, 재고는 있으나 변경 수량보다 적음 → notEnoughStock (둘 다 화면 유지).
    setIsChecking(true)
    try {
      const itemStock = await apiGetSealedItemStock()
      if (!itemStock.isOnSale || itemStock.stock <= 0) {
        setBlockDialog('soldOut')
        return
      }
      if (itemStock.stock < quantity) {
        setBlockDialog('notEnoughStock')
        return
      }
    } catch (error) {
      showToast(getUserFacingMessage(error))
      return
    } finally {
      setIsChecking(false)
    }

    patchOrder(
      {
        orderId: orderInfo.orderId,
        body: { quantity },
      },
      {
        onSuccess: () => {
          showToast('성공적으로 수량이 변경되었어요')
          onCompleted()
        },
        onError: (error) => showToast(getUserFacingMessage(error)),
      },
    )
  }

  return (
    <>
      <EditContainer>
        <EditContents>
          <EditSection>
            <EditSectionHeading>신청 정보</EditSectionHeading>
            <ProductCard
              quantity={quantity}
              onQuantityChange={setQuantity}
              context='edit'
            />
          </EditSection>
        </EditContents>
        <EditBottomBar>
          <CTAButton
            loading={isPending || isChecking}
            disabled={!isChanged}
            onClick={handleSubmit}
          >
            변경 완료
          </CTAButton>
        </EditBottomBar>
      </EditContainer>
      {isEditExpired && (
        <DialogModal
          isOpen
          onClose={onCompleted}
          {...CHANGE_NOT_ALLOWED_DIALOG}
          confirmLabel='확인'
          onConfirm={onCompleted}
        />
      )}
      {blockDialog && (
        <BlockStockAlertModal
          kind={blockDialog}
          onClose={() => setBlockDialog(null)}
        />
      )}
    </>
  )
}
